import AppModal from "@/components/AppModal";
import AppTextField from "@/components/AppTextField";
import RpIcon from "@/components/RpIcon";
import SendCodeButton from "@/components/SendCodeButton";
import AppLoadingButton from "@/components/mui/LoadingButton";
import { VerifyOption } from "@/constant/VerifyConstant";
import use2FA from "@/hook/use2FA";
import useToggle from "@/hook/useToggle";
import { getTaskId, getTaskRuleParams, getToken, setTaskId } from "@/redux/slices/user";
import { Box, InputAdornment, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import classes from "./css/index.module.less";
import PasteButton from "@/components/PasteButton";
import PinInput from "@/components/PinInput.tsx";

function Security2FA({ callback, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sendBtnRef = useRef();
  const [code, setCode] = useState('');
  const {
    toggle: switchOptionOpen,
    onToggle: onToggleSwitchOptionOpen,
  } = useToggle(false);

  const { isAuthenticated } = useSelector(state => state.user);

  const {
    action,
    total, index,
    taskRuleParams,
    setTaskRuleParams,
    loading,
    taskRule,
    verifyLoading,
    currentVerify,
    setCurrentVerify,
    optionVerifyList,
    doVerifyOption,
  } = use2FA({
    callback: callback,
  });

  useEffect(() => {
    const taskRuleParams = getTaskRuleParams();
    if (taskRuleParams == null) {
      // navigate(-1);
      return;
    }

    setTaskId(taskRuleParams.taskId);
    setTaskRuleParams(taskRuleParams);
  }, []);

  const handleSubmit = useCallback(async (evt) => {
    evt.preventDefault();
    await doVerifyOption(code);
    // 清除code输入
    setCode('');
    // 清除验证码发送状态
    sendBtnRef.current?.stopCountdown();
  }, [code]);

  const _switchOption = (option) => {
    setCode('');
    setCurrentVerify(option);
    onToggleSwitchOptionOpen();
    sendBtnRef.current?.stopCountdown();
  }

  const _createOptionList = () => {
    return <div className={classes["model-wrap"]}>
      <Box className={classes["model-title-wrap"]}>
        <Box onClick={onToggleSwitchOptionOpen}><RpIcon icon="close" size={16}/></Box>
        <Box className={classes["title"]}>
          {t('choose_verify_method')}
        </Box>
      </Box>
      <List>
        {optionVerifyList.map((item, idx) => <ListItem key={idx} disablePadding>
          <ListItemButton onClick={() => _switchOption(item)}>
            <ListItemIcon>
              {
                {
                  [VerifyOption.GA]: <RpIcon icon="ga" size={20}/>,
                  [VerifyOption.EMAIL]: <RpIcon icon="email" size={20}/>,
                  [VerifyOption.MOBILE]: <RpIcon icon="mobile" size={20}/>,
                  [VerifyOption.PIN]: <RpIcon icon="lock" size={20}/>,
                }[item] || null
              }
            </ListItemIcon>
            <ListItemText primary={_getTitle(item)}></ListItemText>
          </ListItemButton>
        </ListItem>)}
      </List>
    </div>
  }

  const _createOptionSwitch = () => {
    if (!optionVerifyList.length) return null;
    return <span className={classes["switch-option"]} onClick={onToggleSwitchOptionOpen}>
      {t('switch_option')}
      <RpIcon icon="change-x-copy" size={16}></RpIcon>
    </span>
  }

  const _getTitle = (verifyType) => {
    verifyType = verifyType ?? currentVerify;
    if (verifyType == VerifyOption.GA) {
      return t('google_authenticator_verification');
    } else if (verifyType == VerifyOption.EMAIL) {
      return t('email_verification');
    } else if (verifyType == VerifyOption.MOBILE) {
      return t('mobile_verification');
    } else if (verifyType == VerifyOption.PIN) {
      return t('verify_pin');
    }
    return '';
  }

  const _createCodeInput = () => {
    let inputProps = {};
    if (currentVerify == VerifyOption.EMAIL || currentVerify == VerifyOption.MOBILE) {
      inputProps = {
        endAdornment: (
          <InputAdornment position="end">
            <SendCodeButton
              ref={sendBtnRef}
              btnText={t('send_code')}
              enable={true}
              action={action}
              params={{
                taskId: getTaskId(),
                sign: taskRuleParams?.sign
              }}
              useRecaptcha={!getToken()}
              type={currentVerify === VerifyOption.MOBILE ? 0 : 1}
              onError={(error)=>{
              }}
            />
          </InputAdornment>
        ),
      };
    }
    if (currentVerify == VerifyOption.GA) {
      inputProps = {
        endAdornment: (
          <InputAdornment position="end">
            <PasteButton onPaste={(text) => setCode(text)} />
          </InputAdornment>
        ),
      };
    }
    if (currentVerify == VerifyOption.PIN) {
      return (
        <PinInput
          separator={null}
          length={6}
          onChange={(value) => setCode(value)}
          value={code}
        />
      );
    }
    return <AppTextField
      placeholder={t('verify_code')}
      onChange={(e) => setCode(e.target.value.trim())}
      value={code}
      required
      type="tel"
      fullWidth
      InputProps={inputProps}
    />
  }

  const _promptText = () => {
    let account = '';
    if (currentVerify == VerifyOption.EMAIL) {
      account = taskRule?.email;
    } else if (currentVerify == VerifyOption.MOBILE) {
      account = taskRule?.mobile;
    } else if (currentVerify == VerifyOption.GA) {
      return null;
    } else if (currentVerify == VerifyOption.PIN) {
      return null;
    }
    return <div className={classes["label"]}>
      {t('send_code_to')} {account}
    </div>
  }

  const _createCurrentVerifyForm = () => {
    return <form autoComplete="off" onSubmit={handleSubmit}>
      <div className={classes["model-wrap"]}>
        <Box className={classes["model-title-wrap"]}>
          <Box onClick={onClose}><RpIcon icon="close" size={16}/></Box>
          <Box className={classes["title"]}>
            {_getTitle()} {`${index}/${total}`}
          </Box>
        </Box>
        <Box>
          {_promptText()}
          {_createCodeInput()}
          {_createOptionSwitch()}
        </Box>
        <AppLoadingButton
          fullWidth
          disabled={!code}
          loading={verifyLoading}
          variant="contained"
          color="primary"
          size="large"
          type="submit"
        >
          {t('next')}
        </AppLoadingButton>
      </div>
    </form>;
  }

  if (loading) {
    return <div className={classes["loading-wrap"]}>Loading Rules...</div>
  }

  return (
    <Box>
      {_createCurrentVerifyForm()}
      <AppModal open={switchOptionOpen} onClose={onToggleSwitchOptionOpen}>
        {_createOptionList()}
      </AppModal>
    </Box>
  );
}

export default Security2FA;
